/**
 * ComparisonGridBlock - Compare entities across metrics
 */

import React from 'react'
import { ComparisonGridBlock as ComparisonGridBlockType, MetricValue } from '../../../../lib/cockpit/schemas'

interface ComparisonGridBlockProps {
  data: ComparisonGridBlockType['data']
  blockId: string
  compact?: boolean
}

function formatValue(value: MetricValue): string {
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value !== null && 'formatted' in value) return value.formatted || String(value.value)
  return String(value)
}

function ComparisonGridBlock({ data, blockId, compact }: ComparisonGridBlockProps) {
  const { entities, metrics, values } = data

  if (compact && entities.length > 3) {
    // Show simplified view
    return (
      <div className="flex gap-4 overflow-x-auto pb-2">
        {entities.slice(0, 3).map((entity: any) => (
          <div key={entity.id} className="flex-shrink-0 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg min-w-[150px]">
            <div className="font-medium text-gray-900 dark:text-white text-sm">{entity.name}</div>
            {metrics.slice(0, 2).map((metric: any) => {
              const value = values[entity.id]?.[metric.key]
              return (
                <div key={metric.key} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {metric.label}: {value !== undefined ? formatValue(value) : '—'}
                </div>
              )
            })}
          </div>
        ))}
        {entities.length > 3 && (
          <div className="flex-shrink-0 p-3 flex items-center justify-center text-gray-400">
            +{entities.length - 3} more
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="py-3 px-4 text-left font-medium text-gray-500 dark:text-gray-400">
              Metric
            </th>
            {entities.map((entity: any) => (
              <th key={entity.id} className="py-3 px-4 text-left">
                <div className="flex items-center gap-2">
                  {entity.image_url && (
                    <img src={entity.image_url} alt="" className="w-6 h-6 rounded-full" />
                  )}
                  <span className="font-medium text-gray-900 dark:text-white">{entity.name}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {metrics.map((metric: any) => {
            // Find best value if higher_is_better is set
            let bestEntityId: string | null = null
            if (metric.higher_is_better !== undefined) {
              const numericValues = entities
                .map((e: any) => ({ id: e.id, value: values[e.id]?.[metric.key] }))
                .filter((v: any) => typeof v.value === 'number') as { id: string; value: number }[]
              
              if (numericValues.length > 0) {
                bestEntityId = numericValues.reduce((best, curr) => 
                  metric.higher_is_better 
                    ? (curr.value > best.value ? curr : best)
                    : (curr.value < best.value ? curr : best)
                ).id
              }
            }

            return (
              <tr key={metric.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="py-3 px-4">
                  <div className="font-medium text-gray-700 dark:text-gray-300">{metric.label}</div>
                  {metric.unit && (
                    <div className="text-xs text-gray-500 dark:text-gray-500">{metric.unit}</div>
                  )}
                </td>
                {entities.map((entity: any) => {
                  const value = values[entity.id]?.[metric.key]
                  const isBest = bestEntityId === entity.id
                  
                  return (
                    <td key={entity.id} className={`py-3 px-4 ${isBest ? 'bg-green-50 dark:bg-green-900/20' : ''}`}>
                      <span className={`font-mono ${isBest ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-900 dark:text-white'}`}>
                        {value !== undefined ? formatValue(value) : '—'}
                      </span>
                      {isBest && (
                        <span className="ml-1 text-green-600 dark:text-green-400">✓</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default ComparisonGridBlock



